import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AdminProvider } from './context/AdminContext';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AdminLayout from './pages/admin/AdminLayout';
import OverviewPage from './pages/admin/OverviewPage';
import LinksPage from './pages/admin/LinksPage';
import MembersPage from './pages/admin/MembersPage';
import ImportsPage from './pages/admin/ImportsPage';

const TenantAnalyticsPage = lazy(() => import('./pages/admin/TenantAnalyticsPage'));
const LinkDetailPage = lazy(() => import('./pages/admin/LinkDetailPage'));
const AccessPage = lazy(() => import('./pages/admin/AccessPage'));
const ProfilePage = lazy(() => import('./pages/admin/ProfilePage'));
const WorkspacesPage = lazy(() => import('./pages/admin/WorkspacesPage'));
const SettingsPage = lazy(() => import('./pages/admin/SettingsPage'));
const ImportDetailPage = lazy(() => import('./pages/admin/ImportDetailPage'));
const ImportAnalyticsPage = lazy(() => import('./pages/admin/ImportAnalyticsPage'));
const PlatformPage = lazy(() => import('./pages/admin/PlatformPage'));
const HelpLayout = lazy(() => import('./pages/admin/help/HelpLayout'));
const HelpOverviewPage = lazy(() => import('./pages/admin/help/HelpOverviewPage'));
const HelpPlatformPage = lazy(() => import('./pages/admin/help/HelpPlatformPage'));
const HelpApiPage = lazy(() => import('./pages/admin/help/HelpApiPage'));
const HelpMcpPage = lazy(() => import('./pages/admin/help/HelpMcpPage'));
const HelpTroubleshootingPage = lazy(() => import('./pages/admin/help/HelpTroubleshootingPage'));

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { token } = useAuth();
  return token ? <>{children}</> : <Navigate to="/login" replace />;
}

function RootRedirect() {
  const { token } = useAuth();
  return <Navigate to={token ? '/overview' : '/login'} replace />;
}

function LegacyAppRedirect() {
  const location = useLocation();
  const legacyPath = location.pathname.replace(/^\/app/, '') || '/overview';
  return <Navigate to={`${legacyPath}${location.search}${location.hash}`} replace />;
}

/** Loads the user profile on mount and syncs the i18n language. */
function ProfileSync() {
  const { token, profile, fetchProfile } = useAuth();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (token && !profile) {
      fetchProfile().catch(() => undefined);
    }
  }, [token, profile, fetchProfile]);

  useEffect(() => {
    if (profile?.locale) {
      i18n.changeLanguage(profile.locale).catch(() => undefined);
    }
  }, [profile?.locale, i18n]);

  return null;
}

function App() {
  const fallback = <div className="p-6 text-sm text-muted-foreground">Loading...</div>;

  return (
    <AuthProvider>
      <BrowserRouter>
        <ProfileSync />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          <Route path="/app/*" element={<LegacyAppRedirect />} />

          <Route
            element={(
              <PrivateRoute>
                <AdminProvider>
                  <AdminLayout />
                </AdminProvider>
              </PrivateRoute>
            )}
          >
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="analytics" element={<Suspense fallback={fallback}><TenantAnalyticsPage /></Suspense>} />
            <Route path="overview" element={<OverviewPage />} />
            <Route path="links" element={<LinksPage />} />
            <Route path="links/:urlCode" element={<Suspense fallback={fallback}><LinkDetailPage /></Suspense>} />
            <Route path="imports" element={<ImportsPage />} />
            <Route path="imports/:jobId" element={<Suspense fallback={fallback}><ImportDetailPage /></Suspense>} />
            <Route path="imports/:jobId/analytics" element={<Suspense fallback={fallback}><ImportAnalyticsPage /></Suspense>} />
            <Route path="members" element={<MembersPage />} />
            <Route path="account" element={<Suspense fallback={fallback}><AccessPage /></Suspense>} />
            <Route path="access" element={<Suspense fallback={fallback}><AccessPage /></Suspense>} />
            <Route path="profile" element={<Suspense fallback={fallback}><ProfilePage /></Suspense>} />
            <Route path="workspaces" element={<Suspense fallback={fallback}><WorkspacesPage /></Suspense>} />
            <Route path="platform" element={<Suspense fallback={fallback}><PlatformPage /></Suspense>} />
            <Route path="help" element={<Suspense fallback={fallback}><HelpLayout /></Suspense>}>
              <Route index element={<Suspense fallback={fallback}><HelpOverviewPage /></Suspense>} />
              <Route path="platform" element={<Suspense fallback={fallback}><HelpPlatformPage /></Suspense>} />
              <Route path="api" element={<Suspense fallback={fallback}><HelpApiPage /></Suspense>} />
              <Route path="mcp" element={<Suspense fallback={fallback}><HelpMcpPage /></Suspense>} />
              <Route path="troubleshooting" element={<Suspense fallback={fallback}><HelpTroubleshootingPage /></Suspense>} />
            </Route>
            <Route path="settings" element={<Suspense fallback={fallback}><SettingsPage /></Suspense>} />
          </Route>

          <Route path="*" element={<RootRedirect />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
